import { useState } from 'react';

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';

import { type PatchBagRequest } from './aliases';
import { updateAuthBag } from './api';
import { removeBagItemFromProductList, updateGuestBag } from './helpers';
import { type BagBasicQueryResult } from './types';
import { useBagBasic } from './useBagBasic';

import {
  RQ_BAG_BASIC_KEY,
  RQ_BAG_DETAILED_KEY,
} from '@/modules/ReactQuery/cacheKeys';
import { useCurrentLanguage } from '@/modules/language/useCurrentLanguage';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useDeleteFromBag() {
  useBagBasic();
  const queryClient = useQueryClient();
  const [authenticated] = useCurrentUser();
  const { language } = useCurrentLanguage();
  const t = useTranslations('bag');
  const [errorText, setErrorText] = useState('');

  async function optimisticallyRemoveProducts(mutationData: PatchBagRequest) {
    await queryClient.cancelQueries({
      queryKey: [RQ_BAG_BASIC_KEY],
    });

    const previousData = queryClient.getQueryData<BagBasicQueryResult>([
      RQ_BAG_BASIC_KEY,
    ]);

    if (previousData) {
      const updateProductList = mutationData.reduce(
        (products, product) => removeBagItemFromProductList(products, product),
        previousData.products
      );

      queryClient.setQueryData<BagBasicQueryResult>([RQ_BAG_BASIC_KEY], {
        ...previousData,
        count: previousData?.count
          ? Math.max(Number(previousData.count) - mutationData.length, 0)
          : 0,
        products: updateProductList,
      });
    }
  }

  function onSettled() {
    queryClient.invalidateQueries({
      queryKey: [RQ_BAG_BASIC_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [RQ_BAG_DETAILED_KEY, language],
    });
  }

  function onError() {
    setErrorText(t('Error.DeleteProduct'));
  }

  const deleteFromBagMutation = useMutation({
    mutationFn: updateAuthBag,
    onMutate: async (mutationData) => {
      const previousBasicData =
        await optimisticallyRemoveProducts(mutationData);
      // Return a context object with the snapshotted value
      return { previousBasicData };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_err, _newBagItem, context) => {
      onError();

      if (context?.previousBasicData) {
        queryClient.setQueryData([RQ_BAG_BASIC_KEY], context.previousBasicData);
      }
    },
    onSettled,
  });

  const isDeletingOrDeleted =
    deleteFromBagMutation.isPending || deleteFromBagMutation.isSuccess;

  function deleteFromBag(data: PatchBagRequest) {
    setErrorText('');

    if (authenticated) {
      deleteFromBagMutation.mutate(data);
    } else {
      updateGuestBag(data, {
        onSettled,
        onError,
      });
    }
  }

  return {
    deleteFromBag,
    errorText,
    isDeletingOrDeleted,
  };
}
